import { useCallback } from 'react';

import Stack from '@mui/material/Stack';

import type { Id } from '@e-socna/data-layer';

import { ReorderableCard } from './ReorderableCard';
import { ItemTypes } from './utils/item-types.util';
import type { DraggableItem } from './types/draggable-item.type';
import type { ListProps } from './types/list-props.type';

const isDropAfterItem = (
  draggedItem: DraggableItem,
  targetItem: DraggableItem,
): boolean => draggedItem.index < targetItem.index;

export const List = <I extends object>({
  getLabel,
  idProp,
  indexProp,
  items,
  setItems,
}: Readonly<ListProps<I>>) => {
  const moveItem = useCallback(
    (fromIndex: number, toIndex: number) => {
      const newItems = [...items];
      const [movedItem] = newItems.splice(fromIndex, 1);

      if (!movedItem) {
        return;
      }

      newItems.splice(toIndex, 0, movedItem);

      setItems(
        newItems.map((item, index) => ({ ...item, [indexProp]: index })),
      );
    },
    [items, setItems, indexProp],
  );

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {items.map((item, index) => {
        const id = item[idProp] as Id;

        return (
          <ReorderableCard<DraggableItem>
            disableMoveDown={index === items.length - 1}
            disableMoveUp={index === 0}
            dragItem={{ index }}
            isDropAfter={isDropAfterItem}
            key={id}
            label={getLabel(item)}
            // eslint-disable-next-line react/jsx-no-bind
            onDrop={(draggedItem) => {
              moveItem(draggedItem.index, index);
            }}
            // eslint-disable-next-line react/jsx-no-bind
            onMoveDown={() => {
              moveItem(index, index + 1);
            }}
            // eslint-disable-next-line react/jsx-no-bind
            onMoveUp={() => {
              moveItem(index, index - 1);
            }}
            type={ItemTypes.ITEM}
          />
        );
      })}
    </Stack>
  );
};
